import { useEffect, useMemo, useRef, useState } from 'react';
import { Device, Trigger } from '@/hooks/useTriggerStream';
import { GREENS, hashTo } from './vizPalette';

interface Props {
  devices: Device[];
  triggers: Trigger[];
  lastTrigger: Trigger | null;
  dark: boolean;
}

interface Node {
  id: string;
  x: number;
  y: number;
  isMaster: boolean;
  name: string;
  count: number;
}

interface Pulse {
  from: string;
  to: string;
  start: number;
  key: number;
}

export function ConstellationNetwork({ devices, triggers, lastTrigger, dark }: Props) {
  const W = 1200;
  const H = 700;
  const [now, setNow] = useState(Date.now());
  const [pulses, setPulses] = useState<Pulse[]>([]);
  const pulseKey = useRef(0);

  // Layout: deterministic positions from MAC hash, masters in inner ring, slaves in outer.
  const nodes = useMemo<Node[]>(() => {
    const counts = new Map<string, number>();
    triggers.forEach((t) => counts.set(t.device_id, (counts.get(t.device_id) || 0) + 1));

    const masters = devices.filter((d) => (d.hardware_type || '').toLowerCase().includes('master'));
    const slaves = devices.filter((d) => !(d.hardware_type || '').toLowerCase().includes('master'));
    // Fallback: if no hardware_type, treat all as masters
    const useM = masters.length ? masters : devices;
    const useS = masters.length ? slaves : [];

    const place = (list: Device[], rx: number, ry: number, jitter: number): Node[] =>
      list.map((d, i) => {
        const h = hashTo(d.device_id);
        const baseAngle = (i / Math.max(1, list.length)) * Math.PI * 2;
        const offset = ((h % 1000) / 1000 - 0.5) * jitter;
        const angle = baseAngle + offset;
        return {
          id: d.device_id,
          name: d.name || d.device_id.slice(-5),
          isMaster: (d.hardware_type || '').toLowerCase().includes('master') || !masters.length,
          x: W / 2 + Math.cos(angle) * rx,
          y: H / 2 + Math.sin(angle) * ry,
          count: counts.get(d.device_id) || 0,
        };
      });

    return [...place(useM, 240, 170, 0.25), ...place(useS, 480, 300, 0.18)];
  }, [devices, triggers]);

  // Edges: simple — each slave connects to nearest master.
  const edges = useMemo(() => {
    const masters = nodes.filter((n) => n.isMaster);
    const slaves = nodes.filter((n) => !n.isMaster);
    if (!masters.length || !slaves.length) {
      // Otherwise: connect each node to its 2 nearest neighbors for a delicate web.
      const out: { from: string; to: string }[] = [];
      nodes.forEach((n) => {
        const sorted = nodes
          .filter((o) => o.id !== n.id)
          .map((o) => ({ o, d: (o.x - n.x) ** 2 + (o.y - n.y) ** 2 }))
          .sort((a, b) => a.d - b.d)
          .slice(0, 2);
        sorted.forEach(({ o }) => out.push({ from: n.id, to: o.id }));
      });
      return out;
    }
    return slaves.map((s) => {
      const nearest = masters.reduce((best, m) => {
        const d = (m.x - s.x) ** 2 + (m.y - s.y) ** 2;
        return d < best.d ? { m, d } : best;
      }, { m: masters[0], d: Infinity }).m;
      return { from: nearest.id, to: s.id };
    });
  }, [nodes]);

  const nodeMap = useMemo(() => {
    const m = new Map<string, Node>();
    nodes.forEach((n) => m.set(n.id, n));
    return m;
  }, [nodes]);

  // Animation tick
  useEffect(() => {
    let raf: number;
    const tick = () => {
      setNow(Date.now());
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Spawn a pulse when a new trigger arrives
  useEffect(() => {
    if (!lastTrigger) return;
    const matchingEdges = edges.filter((e) => e.from === lastTrigger.device_id || e.to === lastTrigger.device_id);
    const newPulses = matchingEdges.map((e) => ({
      from: e.from,
      to: e.to,
      start: Date.now(),
      key: ++pulseKey.current,
    }));
    setPulses((prev) => [...prev, ...newPulses]);
  }, [lastTrigger, edges]);

  // Garbage-collect old pulses
  useEffect(() => {
    if (!pulses.length) return;
    const t = setTimeout(() => {
      setPulses((prev) => prev.filter((p) => now - p.start < 2200));
    }, 500);
    return () => clearTimeout(t);
  }, [pulses, now]);

  const stroke = dark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.22)';
  const textCol = dark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';
  const maxCount = Math.max(1, ...nodes.map((n) => n.count));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ maxHeight: '80vh' }}>
      <defs>
        <radialGradient id="nodeGlow">
          <stop offset="0%" stopColor="var(--viz-accent)" stopOpacity="0.9" />
          <stop offset="100%" stopColor="var(--viz-accent)" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Edges */}
      {edges.map((e, i) => {
        const a = nodeMap.get(e.from);
        const b = nodeMap.get(e.to);
        if (!a || !b) return null;
        return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={stroke} strokeWidth={0.8} />;
      })}

      {/* Pulses traveling along edges */}
      {pulses.map((p) => {
        const a = nodeMap.get(p.from);
        const b = nodeMap.get(p.to);
        if (!a || !b) return null;
        const t = Math.min(1, (now - p.start) / 1600);
        const x = a.x + (b.x - a.x) * t;
        const y = a.y + (b.y - a.y) * t;
        return (
          <g key={p.key}>
            <circle cx={x} cy={y} r={10} fill="var(--viz-accent)" opacity={(1 - t) * 0.4} />
            <circle cx={x} cy={y} r={3.5} fill="var(--viz-2)" opacity={1 - t} />
          </g>
        );
      })}

      {/* Nodes */}
      {nodes.map((n) => {
        const breath = 0.5 + 0.5 * Math.sin(now / 1400 + hashTo(n.id) % 100);
        const intensity = Math.min(1, n.count / maxCount);
        const r = n.isMaster ? 9 : 6;
        const glow = 24 + intensity * 30 + breath * 6;
        const colorIdx = Math.min(GREENS.length - 1, 3 + Math.floor(intensity * 5));
        return (
          <g key={n.id}>
            <circle cx={n.x} cy={n.y} r={glow} fill="url(#nodeGlow)" opacity={0.15 + intensity * 0.5} />
            <circle
              cx={n.x}
              cy={n.y}
              r={r}
              fill={GREENS[colorIdx]}
              stroke={dark ? '#fff' : '#000'}
              strokeWidth={0.8}
            />
            {n.isMaster && (
              <text
                x={n.x}
                y={n.y + r + 14}
                textAnchor="middle"
                fontSize={9}
                fill={textCol}
                fontFamily="ui-monospace, monospace"
              >
                {n.name.slice(-8)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
